import { derived, get, writable } from "svelte/store";
import { activityFeedStore, todosModalItemStore } from "../../stores";

import { uuidV4 } from "@sourcewhale/shared/src/assets/js/src/general/utils";
import { addWebsocketListener } from "@sourcewhale/shared/src/assets/js/src/general/websocket";
import { SwDerivedApiStore } from "@sourcewhale/shared/src/svelte/api-stores/sw-api-store";
import {
  contactChatAsk,
  contactChatGet,
} from "../../../../../api/contact-chat";

export const CONTACT_AI_CONTEXT = Symbol("contact-ai-context");

class ChatStore {
  #store = writable({});

  set(task) {
    this.#store.set({
      regenerate: false,
      task,
    });
  }

  update(callback) {
    this.#store.update((current) => {
      const task = callback(current.task || "");
      return { regenerate: false, task };
    });
  }

  subscribe(callback) {
    return this.#store.subscribe((current) => {
      callback(current);
    });
  }

  regenerate() {
    return this.#store.update((current) => ({
      regenerate: true,
      task: current.task,
    }));
  }
}

export const createContactAiContext = () => {
  const chatStore = new ChatStore();

  const thinking = writable(false);

  let chatId = null;
  const withChatIdUpdate =
    (f) =>
    (...args) =>
      f(...args).then((res) => {
        if (res.chatId) {
          chatId = res.chatId;
        }
        return res;
      });

  const getArgsStore = derived(todosModalItemStore, ($todosModalItemStore) => {
    if (!$todosModalItemStore) return null;
    const { candidateId } = $todosModalItemStore;
    return candidateId ? [{ candidateId }] : null;
  });

  const contactChatGetApiStore = SwDerivedApiStore({
    get: withChatIdUpdate(contactChatGet),
    argsStore: getArgsStore,
  });

  const askArgsStore = derived(
    [todosModalItemStore, chatStore, contactChatGetApiStore],
    ([$todosModalItemStore, $chatStore, $contactChatGetApiStore]) => {
      if (!$chatStore.task && !$chatStore.regenerate) return null;

      if (!$todosModalItemStore) return null;
      const { candidateId } = $todosModalItemStore;
      if (!candidateId) return null;

      // prompt will always after get successful call. Wait for this to complete before allowing posting.
      if (!$contactChatGetApiStore.prompt) return null;

      const { feed } = get(activityFeedStore);

      // force random text for regenerate as backend ignores prompt if regenerating
      const task = $chatStore.regenerate ? uuidV4() : $chatStore.task;
      const { regenerate } = $chatStore;

      const args = [
        {
          candidateId,
          task,
          feed,
          regenerate,
        },
      ];
      // continue previous chat if it exists
      if (chatId) args[0].chatId = chatId;
      return args;
    },
  );

  const asyncContactChatStore = SwDerivedApiStore({
    get: withChatIdUpdate(contactChatAsk),
    argsStore: askArgsStore,
    resetBeforeFetch: false,
    init: () => ({ prompt: null }),
  });

  const contactChatAskApiStore = writable(null);

  askArgsStore.subscribe((args) => {
    if (args) thinking.set(true);
  });
  let candidateId = null;
  todosModalItemStore.subscribe((toDo) => {
    const newCandidateId = toDo?.candidateId || null;
    if (candidateId !== newCandidateId) {
      chatStore.set("");
      chatId = null;
      asyncContactChatStore.reset();
      candidateId = newCandidateId;
    }
  });

  const defaultThinkingMessage = "Thinking about your question...";
  const contactChatThinkingStore = writable(defaultThinkingMessage);

  addWebsocketListener((message) => {
    if (message.kind === "contact_chat") {
      contactChatThinkingStore.set(message.message);
    } else if (message.kind === "contact_chat_response") {
      contactChatAskApiStore.set(message);
      if (message?.chatId) chatId = message.chatId;
      thinking.set(false);
    }
  });

  // Reset when user submits a new query
  chatStore.subscribe((chat) => {
    if (chat.task) {
      contactChatThinkingStore.set(defaultThinkingMessage);
    }
  });

  const promptStore = derived(
    [contactChatGetApiStore, contactChatAskApiStore],
    ([$get, $ask]) => {
      const rawPrompt = $ask?.prompt || $get?.prompt || [];

      return rawPrompt.map((msg) => {
        if (msg.role === "assistant" && typeof msg.content === "string") {
          try {
            return { ...msg, content: JSON.parse(msg.content.trim()) };
          } catch {
            return msg;
          }
        }
        return msg;
      });
    },
  );

  return {
    chatStore,
    contactChatAskApiStore,
    contactChatThinkingStore: { subscribe: contactChatThinkingStore.subscribe },
    thinking,
    promptStore,
  };
};
